import React, { useMemo } from 'react';
import clsx from 'clsx';
import { MINTING_INFO_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import { AudienceCommentCard } from '@components/CommentCard/AudienceCommentCard';
import {
  GetNotifiedTogglePanel,
  GetNotifiedTogglePanelButton,
} from '@components/EmailNotifications/GetNotifiedTogglePanel';
import { useGetNotified } from '@components/EmailNotifications/useGetNotified';
import { Button } from '@components/main-components/button/Button';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { Skeleton } from '@components/main-components/skeleton/Skeleton';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  GetNftForShareToSocialDocument,
  gql,
  ShareToSocialModalNftInfoFragmentDoc,
  ShareToSocialModalReleaseInfoFragmentDoc,
  useQuery,
} from '@graphql/reactQuery';
import { useCheckReferrableRelease } from '@hooks/useCheckReferrable';
import { useLogInfo } from '@hooks/useLogInfo';

import { useUrlWithAuthReferral } from '@hooks/useUrlWithReferral';
import { TIER_NUMBERS } from '@utils/tiers';
import { parseCommentType } from '../../../types/comments';
import { StatusBar } from './StatusBar';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ShareToSocialModalReleaseInfo on Release {
    id
    title
    webappUri
    artist {
      id
      soundHandle
      name
      user {
        id
        twitterHandle
      }
    }
    ...ReleaseCommentType
    ...ReleaseReferrableInfo
    ...DynamicFeePercentInfo
  }
  fragment ShareToSocialModalNftInfo on Nft {
    id
    serialNumber
    tierNumber
    isGoldenEgg
    collectorRelease {
      id
      nftsCount
    }
    ...NftCommentType
  }
  query GetNFTForShareToSocial($id: UUID!) {
    nftFromId(id: $id) {
      id
      ...ShareToSocialModalNftInfo
      release {
        id
        artist {
          id
          user {
            id
          }
        }
        ...ShareToSocialModalReleaseInfo
      }
    }
  }
`);

const pillar = PILLARS.MINTING;
const isArtistOnlyPage = false;

const ShareToSocialCTA = ({
  release: releaseFragment,
  nft: nftFragment,
  subscribeToggled,
}: {
  release: FragmentType<ShareToSocialModalReleaseInfoFragmentDoc>;
  nft: FragmentType<ShareToSocialModalNftInfoFragmentDoc>;
  subscribeToggled: boolean;
}) => {
  const { tablet } = MediaQuery.useContainer();
  const logInfo = useLogInfo();

  const release = getFragment(ShareToSocialModalReleaseInfoFragmentDoc, releaseFragment);
  const nft = getFragment(ShareToSocialModalNftInfoFragmentDoc, nftFragment);

  const { isShareReferrable } = useCheckReferrableRelease({
    release,
  });

  const { urlWithReferral } = useUrlWithAuthReferral({
    url: release.webappUri,
    source: {
      link: true,
    },
    isReferrable: isShareReferrable,
  });

  const artist = release?.artist;

  const handleShareToTwitter = () => {
    if (!release || !artist) return;

    const artistTwitterHandle = artist?.user.twitterHandle;

    const path = new URL('https://twitter.com/intent/tweet');

    path.searchParams.set(
      'text',
      `Just became the #${nft.serialNumber} backer of ${release.title} from ${
        artistTwitterHandle ? '@' + artistTwitterHandle : artist.name || artist.soundHandle || ''
      } on @soundxyz_\n${urlWithReferral}`,
    );

    logInfo({
      action: MINTING_INFO_ACTIONS.SHARE_COMMENT_TO_TWITTER,
      message: `Shared comment to twitter for release ${release.id} and nft ${nft.id}`,
      data: {
        releaseId: release.id,
        nftId: nft.id,
        urlWithReferral,
      },
      pillar,
      isArtistOnlyPage,
    });

    window.open(path, '_newtab');
  };

  return (
    <div className="flex w-full justify-end py-1">
      <GetNotifiedTogglePanelButton
        artistId={release.artist.id}
        userId={release.artist.user.id}
        subscribeToggled={subscribeToggled}
      />
      <Button
        fullWidth={!tablet}
        label={isShareReferrable ? `Share & Earn` : 'Share on Twitter'}
        variant="primary"
        onClick={handleShareToTwitter}
      />
    </div>
  );
};

const ShareToSocialBody = ({
  release: releaseFragment,
  nft: nftFragment,
  subscribeToggled,
  setSubscribeToggle,
}: {
  release: FragmentType<ShareToSocialModalReleaseInfoFragmentDoc>;
  nft: FragmentType<ShareToSocialModalNftInfoFragmentDoc>;
  subscribeToggled: boolean;
  setSubscribeToggle: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const release = getFragment(ShareToSocialModalReleaseInfoFragmentDoc, releaseFragment);
  const nft = getFragment(ShareToSocialModalNftInfoFragmentDoc, nftFragment);

  const comment = useMemo(
    () =>
      parseCommentType({
        nft,
        release,
      }),
    [nft, release],
  );

  const datePurchased = new Date(comment.updatedAt ?? 0);
  const edition = nft.isGoldenEgg
    ? 'goldenEgg'
    : nft.tierNumber === TIER_NUMBERS.LIMITED
    ? 'vip'
    : 'ga';

  return (
    <div className="flex flex-col gap-3 md:gap-6 md:pb-0">
      <StatusBar releaseId={release.id} />
      <div className="w-full">
        <div
          className={clsx(
            'mx-auto flex h-full w-full items-center justify-start bg-neutral900 px-2 py-1',
            'rounded-md text-base-xs font-medium text-white',
          )}
        >
          <AudienceCommentCard
            username={comment.username}
            publicAddress={comment.publicAddress}
            comment={comment.commentText}
            date={datePurchased.toISOString()}
            avatarUrl={comment.avatarUrl || undefined}
            isArtist={comment.isArtist}
            webappUri={comment.webappUri}
            edition={edition}
            sort="top"
            totalEditions={nft.collectorRelease?.nftsCount}
            className="w-full !max-w-none"
          />
        </div>
      </div>

      <GetNotifiedTogglePanel
        artistId={release.artist.id}
        userId={release.artist.user.id}
        subscribeToggled={subscribeToggled}
        toggleSubscribe={setSubscribeToggle}
      />
      <ShareToSocialCTA
        release={releaseFragment}
        nft={nftFragment}
        subscribeToggled={subscribeToggled}
      />
    </div>
  );
};

export function ShareToSocialModal({ releaseId, nftId }: { releaseId: string; nftId: string }) {
  const { closeCancelModal } = ModalContext.useContainer();
  const { tablet } = MediaQuery.useContainer();
  const [subscribeToggled, setSubscribeToggle] = React.useState(true);

  const {
    data: nftData,
    isLoading: isNftLoading,
    isError: isNftError,
    refetch: refetchNft,
  } = useQuery(GetNftForShareToSocialDocument, {
    variables: { id: nftId },
    staleTime: 0,
  });

  const release = nftData?.data?.nftFromId?.release;

  const { needsVerification, isSubscribedToArtist, handleSubscribe, handleEmailVerification } =
    useGetNotified({
      artistId: release?.artist.id,
      userId: release?.artist.user.id,
    });

  const onCloseModal = () => {
    closeCancelModal();

    if (!subscribeToggled) {
      return;
    }

    if (needsVerification) {
      handleEmailVerification();
    }

    if (!isSubscribedToArtist) {
      handleSubscribe();
    }
  };

  if (isNftLoading || !nftData?.data.nftFromId) {
    return (
      <StandardModal
        title="Loading..."
        body={
          <div className="flex flex-col gap-3 pt-4 md:gap-6 md:pb-0 md:pt-6">
            <StatusBar releaseId={releaseId} />
            <Skeleton className="h-48 w-full" />
          </div>
        }
        closeModal={closeCancelModal}
        size="medium"
        isFullScreen={!tablet}
      />
    );
  }

  if (isNftError) {
    return (
      <StandardModal
        title="Oops, something went wrong!"
        body={
          <div className="flex flex-col gap-3 pt-4 md:gap-6 md:pb-0 md:pt-6">
            <StatusBar releaseId={releaseId} />
            <div className="w-full">
              <p>There was an error loading the data. Please try again.</p>
            </div>
            <div className="flex justify-end">
              <Button
                fullWidth={!tablet}
                label="Retry"
                variant="primary"
                onClick={() => {
                  if (isNftError) refetchNft();
                }}
              />
            </div>
          </div>
        }
        closeModal={closeCancelModal}
        size="medium"
        isFullScreen={!tablet}
        bodyWithoutPaddingBottom
      />
    );
  }

  return (
    <StandardModal
      title="You're all set!"
      body={
        <ShareToSocialBody
          release={nftData.data.nftFromId.release}
          nft={nftData.data.nftFromId}
          subscribeToggled={subscribeToggled}
          setSubscribeToggle={setSubscribeToggle}
        />
      }
      closeModal={onCloseModal}
      bodyWithoutPaddingBottom
      size="medium"
      isFullScreen={!tablet}
    />
  );
}
